import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MobileView, StyledContent, StyledLayout, WebView } from "../Category/styles";
import { CollapseSection, ItemColumn, ItemLabel, ItemList } from "./DeleteReasonConst";
import ConfirmationModal from "../../components/ConfirmationModal";
import EditReasonModal from "./EditReasonModal";
import { Form } from "antd";
import { selectDeleteReason } from "../../redux/features/deleteReason/deleteReasonSlice";
import { fetchDeleteReasons, addDeleteReason, updateDeleteReason, deleteDeleteReason } from "../../redux/features/deleteReason/deleteReasonAsyncThunk";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/redux";
import { RequestResults } from "../../redux/common/RequestStatus";
import { ReasonStatus, ReasonType } from "../../types/ReasonTypes";
import { selectUserRole } from "../../redux/features/auth/selectors";
import { checkHasPermission } from "../../utils/helpers/checkHasPermission";
import { permissions } from "../../utils/constants/permissions";

const DeleteReason = () => {
  const dispatch = useAppDispatch();
  const { data, loading, status } = useAppSelector(selectDeleteReason);
  const [form] = Form.useForm();

  // Modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);

  const userRole = useAppSelector(selectUserRole);

  const hasPermission = useCallback(
    (permissionKey: string) => checkHasPermission(userRole, permissionKey),
    [userRole]
  );

  const isAddPermission = useMemo(() => hasPermission(permissions.AddEditDeleteReason), [hasPermission]);
  const isEditPermission = useMemo(() => hasPermission(permissions.AddEditDeleteReason), [hasPermission]);
  const isViewPermission = useMemo(() => hasPermission(permissions.ViewDeleteReason), [hasPermission]);
  const isDeletePermission = useMemo(() => hasPermission(permissions.DeleteDeleteReason), [hasPermission]);


  useEffect(() => {
    if (status === RequestResults.idle) {
      dispatch(fetchDeleteReasons() as any);
    }
  }, [dispatch, status]);

  const dataSource = useMemo(() => {
    return data.map((item: any) => ({
      ...item,
      name: item.reason,
      key: item.id,
    }));
  }, [data]);

  const handleAddReason = () => {
    setSelectedItem(null);
    setIsEditModalOpen(true);
    form.resetFields();
  };

  const handleEditReason = (item: any) => {
    setSelectedItem(item);
    setIsEditModalOpen(true);
    form.setFieldsValue({
      reason: item.reason,
      showInPortal: !!item.showInPortal,
    });
  };

  const handleDeleteReason = (item: any) => {
    setSelectedItem(item);
    setIsDeleteModalOpen(true);
  };

  const handleSaveReason = async (values: any) => {
    const showInPortal = !!values.showInPortal;
    if (selectedItem?.id) {
      await dispatch(updateDeleteReason({ id: selectedItem.id, ...values, showInPortal, status: selectedItem.status }));
    } else {
      await dispatch(addDeleteReason({ ...values, showInPortal, status: ReasonStatus.Active, type: ReasonType.Lost }));
    }
    setIsEditModalOpen(false);
    form.resetFields();
  };

  const handleConfirmDelete = async () => {
    if (selectedItem) {
      await dispatch(deleteDeleteReason(selectedItem.id) as any);
      setIsDeleteModalOpen(false);
    }
  };

  return (
    <StyledLayout>
      <WebView>
        <StyledContent>
          <ItemColumn
            hasAddPermission={isAddPermission}
            hasEditPermission={isEditPermission}
            hasDeletePermission={isDeletePermission}
            hasViewPermission={isViewPermission}
            title="Delete Reason"
            buttonLabel="+ Add Reason"
            onAddClick={handleAddReason}
            loading={status === RequestResults.pending}
            dataSource={dataSource}
            isSelectedItemOnClick={(item: any) => setSelectedItem(item)}
            onEdit={handleEditReason}
            onDelete={handleDeleteReason}
          />
        </StyledContent>
      </WebView>

      <MobileView>
        <CollapseSection
          itemKey="1"
          activeKey="1"
          label={<ItemLabel title="Delete Reason" buttonLabel="+ Add Reason" onButtonClick={handleAddReason} hasAddPermission={isAddPermission} />}
          children={
            <ItemList
              hasAddPermission={isAddPermission}
              hasEditPermission={isEditPermission}
              hasDeletePermission={isDeletePermission}
              hasViewPermission={isViewPermission}
              loading={status === RequestResults.pending} dataSource={dataSource} isSelectedItemOnClick={(item: any) => setSelectedItem(item)} onEdit={handleEditReason} onDelete={handleDeleteReason} onItemClick={() => { }} />
          }
        />
      </MobileView>

      <EditReasonModal
        form={form}
        isModalOpen={isEditModalOpen}
        onCancel={() => {
          setIsEditModalOpen(false);
          setSelectedItem(null);
          form.resetFields();
        }}
        handleSave={handleSaveReason}
        hasEditPermission={isEditPermission}
        hasViewPermission={isViewPermission}
        hasAddPermission={isAddPermission}
      />

      {isDeleteModalOpen && <ConfirmationModal
        handleSave={handleConfirmDelete}
        isModalOpen={isDeleteModalOpen}
        onCancel={() => setIsDeleteModalOpen(false)}
        title="Delete Reason"
        message={`Are you sure you want to delete the Reason: ${selectedItem?.name}?`}
        confirmButtonText="Delete"
        loading={loading}
      />}

      {isStatusModalOpen && <ConfirmationModal
        hasEditPermission={isEditPermission}
        hasViewPermission={isViewPermission}
        hasAddPermission={isAddPermission}
        handleSave={() => { }}
        isModalOpen={isStatusModalOpen}
        onCancel={() => setIsStatusModalOpen(false)}
        title={`Confirm ${selectedItem?.checked ? "Deactivation" : "Activation"} Reason`}
        message={`Are you sure you want to ${selectedItem?.checked ? "deactivate" : "activate"} this reason?`}
        confirmButtonText={selectedItem?.checked ? "Deactivate" : "Activate"}
        loading={loading}
      />}
    </StyledLayout>
  );
};

export default DeleteReason;
